import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Scale, Youtube, Facebook, Instagram, Linkedin, Twitter, Clock } from "lucide-react";
import { useSettings, telHref } from "@/context/SettingsContext";

const SOCIAL = [
  { key: "youtube", Icon: Youtube }, { key: "facebook", Icon: Facebook },
  { key: "instagram", Icon: Instagram }, { key: "linkedin", Icon: Linkedin },
  { key: "twitter", Icon: Twitter },
];

export function FooterCTA() {
  const settings = useSettings();
  const c = settings?.contact || {};
  return (
    <section className="bg-navy-light border-y border-brass/20" data-testid="footer-cta">
      <div className="max-w-5xl mx-auto px-6 py-14 lg:py-20 text-center">
        <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-4">Legal Assistance</p>
        <h2 className="text-ivory text-3xl lg:text-4xl font-serif mb-4">Need Legal Assistance?</h2>
        <p className="text-ivory/70 text-base lg:text-lg mb-8 max-w-2xl mx-auto">Submit your enquiry to discuss your legal matter with Adv. Guruvayoorappan K V.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link to="/contact" className="bg-brass hover:bg-brass-hover text-navy font-semibold px-8 py-3.5 rounded-sm transition-colors tracking-wide w-full sm:w-auto" data-testid="footer-cta-enquiry">
            Send an Enquiry
          </Link>
          <a href={telHref(c.phone1)} className="border border-brass/50 text-brass hover:bg-brass/10 font-semibold px-8 py-3.5 rounded-sm transition-colors tracking-wide flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="footer-cta-call">
            <Phone size={17} /> Call Now
          </a>
        </div>
        <p className="text-ivory/50 text-sm mt-6 font-mono">{c.phone1 || "+91 75598 97677"} &nbsp;·&nbsp; {c.phone2 || "+91 96457 99817"}</p>
      </div>
    </section>
  );
}

export function Footer() {
  const settings = useSettings();
  const c = settings?.contact || {};
  const social = settings?.social || {};
  const year = new Date().getFullYear();
  return (
    <footer className="bg-navy-dark text-ivory/70" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-9 h-9 rounded-sm border border-brass/50 flex items-center justify-center text-brass"><Scale size={18} /></span>
          </div>
          <h3 className="text-ivory font-serif text-lg">ADV. GURUVAYOORAPPAN K V</h3>
          <p className="text-brass text-xs font-mono tracking-widest uppercase mt-1 mb-4">Advocate · Thrissur, Kerala</p>
          <div className="flex gap-3">
            {SOCIAL.filter(({ key }) => social[key]).map(({ key, Icon }) => (
              <a key={key} href={social[key]} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-sm border border-ivory/15 flex items-center justify-center hover:border-brass hover:text-brass transition-colors" data-testid={`footer-social-${key}`}>
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-ivory text-sm font-semibold tracking-widest uppercase mb-4">Office</h4>
          <p className="flex gap-2 text-sm leading-relaxed"><MapPin size={16} className="text-brass shrink-0 mt-0.5" /><span>{c.address || "1st Floor, KMA Towers, Kanjani Road, Thrissur-4, Chungam, West Fort, Thrissur, Kerala – 680004"}</span></p>
          <p className="flex items-center gap-2 text-sm mt-3"><Clock size={16} className="text-brass shrink-0" /><span>{c.office_hours}</span></p>
        </div>

        <div>
          <h4 className="text-ivory text-sm font-semibold tracking-widest uppercase mb-4">Contact</h4>
          <a href={telHref(c.phone1)} className="flex items-center gap-2 text-sm hover:text-brass transition-colors" data-testid="footer-phone1"><Phone size={15} className="text-brass" />{c.phone1 || "+91 75598 97677"}</a>
          <a href={telHref(c.phone2)} className="flex items-center gap-2 text-sm mt-2 hover:text-brass transition-colors" data-testid="footer-phone2"><Phone size={15} className="text-brass" />{c.phone2 || "+91 96457 99817"}</a>
          <p className="flex items-center gap-2 text-sm mt-2"><Mail size={15} className="text-brass" />{c.email && c.email.includes("@") ? <a href={`mailto:${c.email}`} className="hover:text-brass transition-colors" data-testid="footer-email">{c.email}</a> : c.email}</p>
        </div>

        <div>
          <h4 className="text-ivory text-sm font-semibold tracking-widest uppercase mb-4">Navigate</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-brass transition-colors">Home</Link></li>
            <li><Link to="/about" className="hover:text-brass transition-colors">Profile</Link></li>
            <li><Link to="/practice-areas" className="hover:text-brass transition-colors">Practice Areas</Link></li>
            <li><Link to="/blog" className="hover:text-brass transition-colors">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-brass transition-colors">Contact</Link></li>
            <li><Link to="/privacy-policy" className="hover:text-brass transition-colors">Privacy Policy</Link></li>
            <li><Link to="/disclaimer" className="hover:text-brass transition-colors">Disclaimer</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-ivory/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-ivory/50">
          <p>© {year} Adv. Guruvayoorappan K V. All rights reserved.</p>
          <p className="text-center sm:text-right">Content is for general information only and does not constitute legal advice.</p>
        </div>
      </div>
    </footer>
  );
}
